/**
 * 「内容使用说明」页的服务列表（DATA-USE-COPY-01；c84a REPLY-I-20261009-31 第 29 项、-32 05:46Z 补充裁定；I 10-09 派）。
 * 数据来自公开只读的 GET /api/v1/web/meta/data-services（adm1 DATA-USE-SERVICES-01-R1，已叠进 I108）：实际在用的外部服务，每项
 * purpose（闭集）、display_name（可能为空：走中转、或地址对不上品牌表）、contract_entity（目前一律为空，页面不显示）。
 *
 * **等 I108 契约同步换掉**：generated.ts 里还没有这个接口，下面的类型是就近手写的（不改 generated.ts）。
 * 回包只认 { services: [...] }（以契约为准，6c2b 10-09 定）：裸数组和别的形状一律当读不到，不猜、不编，也免得以后两种形状都要维护。
 * 契约到了以后：类型换成契约的。
 *
 * 规矩（I 10-09；6c2b 10-09 定第二版）：
 * - 只列接口里真有的用途；不认识的 purpose 不显示、也不让页面出错。
 * - display_name 为空：照样列这一项，只写用途、写“交给外部服务处理”，不写“暂无”“未知”之类。
 * - contract_entity 不上页面（品牌名写成“目前在用：<名字>”，不说成合同主体）；display_name 照后端原样显示，不过滤。
 */

/** 接口里的一项（等 I108 契约同步换掉） */
export interface DataServiceItem {
  /** conversation / images / photo_check / maps / travel_research / verification_sms / verification_email */
  purpose: string;
  /** 给人看的服务名；走中转、对不上品牌表时为空 */
  display_name: string | null;
  /** 合同主体；目前一律为空，页面不显示 */
  contract_entity: string | null;
}

/** GET /meta/data-services 的回包（等 I108 契约同步换掉） */
export interface DataServicesView {
  services: DataServiceItem[];
}

/** 用途闭集（I 10-09 转 adm1 的那几个值），顺序就是页面上的顺序：先说和主人交流、照片有关的，再说地图、攻略，最后是验证码 */
export const DATA_SERVICE_PURPOSES = ["conversation", "images", "photo_check", "maps", "travel_research", "verification_sms", "verification_email"] as const;
export type DataServicePurpose = (typeof DATA_SERVICE_PURPOSES)[number];
/** 上传照片处原地打开的面板只列这两种（图像服务、检查照片；I 10-09，DATA-USE-COPY-01-R1） */
export const IMAGE_PURPOSES: readonly DataServicePurpose[] = ["images", "photo_check"];

/** 用途写成人话（对照表交 6c2b 定）：不写品牌、不写技术词；也不写“上传”（说明会在形象页上原地打开，那一页整页不出现“上传”） */
export const PURPOSE_LABEL: Readonly<Record<DataServicePurpose, string>> = {
  conversation: "对话与写信措辞",
  images: "TA 的样子与照片",
  photo_check: "检查照片",
  maps: "地图与地点",
  travel_research: "旅行攻略里的地点资料",
  verification_sms: "手机验证码",
  verification_email: "邮箱验证码",
};

/** 有名字时那一行写什么 */
export function inUseText(name: string): string {
  return `目前在用：${name}`;
}
/** 没有名字时那一行写什么：只说交给外部服务，不写“暂无” */
export const NAMELESS_TEXT = "交给外部服务处理";

export interface DataServiceRow {
  purpose: DataServicePurpose;
  label: string;
  /** 给人看的那一行：有名字是“目前在用：<名字>”，没有是“交给外部服务处理” */
  text: string;
}

/**
 * 把回包整理成页面上的几行：只按用途闭集逐个去找（所以不认识的用途、缺 purpose 的项自然不出现）；按闭集的顺序排，同一用途里保持接口给的顺序；
 * 名字去掉首尾空白，空串当没有名字；同一用途同一个说法只留一行。给了 only 就只列这几种用途（面板里只要图像两种），别的规则一样。
 * 回包不是 { services: [...] }（裸数组也算）返回 null，由页面照实说读不到。
 */
export function readDataServices(raw: unknown, only?: readonly DataServicePurpose[]): DataServiceRow[] | null {
  const list: unknown = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as { services?: unknown }).services : undefined;
  if (!Array.isArray(list)) return null;
  const rows: DataServiceRow[] = [];
  for (const purpose of DATA_SERVICE_PURPOSES) {
    if (only && !only.includes(purpose)) continue;
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const entry = item as Partial<DataServiceItem>;
      if (entry.purpose !== purpose) continue;
      const name = typeof entry.display_name === "string" ? entry.display_name.trim() : "";
      const text = name ? inUseText(name) : NAMELESS_TEXT;
      if (rows.some((row) => row.purpose === purpose && row.text === text)) continue;
      rows.push({ purpose, label: PURPOSE_LABEL[purpose], text });
    }
  }
  return rows;
}

/** 查询键：共享键表（shared/query）不在本包范围，就近定义；前缀 identity 与设置页一致。接口是公开的，不按账号分键。 */
export const DATA_SERVICES_KEY = ["identity", "data-services"] as const;
